import type { AmsMessageKey } from '../../../messages.ts';

/**
 * Operator → Team (Rule Book 3.12: "manage operator team", "reset MFA and
 * lock"). The pure logic of the section, kept out of the .tsx so a test can
 * read it without a DOM. Its words are in messages/operators.ts under
 * `ops.team.`.
 *
 * What a row offers follows two things only: what the viewer holds and the
 * state the member is in. The server checks both again; this only keeps a
 * button off the screen that would be refused.
 */

export interface TeamMember {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  /** ACTIVE, SUSPENDED (a locked login) or CLOSED (disabled). */
  readonly status: string;
  readonly roles: readonly string[];
  readonly mfa_enrolled: boolean;
  readonly must_change_password: boolean;
  readonly brands: readonly { readonly id: string; readonly name: string }[];
  readonly is_owner: boolean;
}

export interface TeamData {
  readonly operator: string;
  readonly items: readonly TeamMember[];
  readonly mfa_enforced: boolean;
  /** The roles a member may be given — never the owner role. */
  readonly roles: readonly { readonly id: string; readonly code: string }[];
  readonly brands: readonly { readonly id: string; readonly name: string }[];
}

export interface TeamCapabilities {
  readonly view: boolean;
  readonly manage: boolean;
  readonly credentials: boolean;
  /** Delete a member for good: team management AND the purge capability. */
  readonly delete: boolean;
}

/** From `mine` on GET /v1/platform/permissions. */
export function teamCapabilities(mine: readonly string[]): TeamCapabilities {
  const held = new Set(mine);
  const manage = held.has('operator_team.manage');
  return {
    view: held.has('operators.view'),
    manage,
    credentials: held.has('credentials.reset'),
    delete: manage && held.has('entities.purge'),
  };
}

export type SignIn = 'active' | 'locked' | 'disabled';

export function signInState(m: Pick<TeamMember, 'status'>): SignIn {
  if (m.status === 'CLOSED') return 'disabled';
  if (m.status === 'SUSPENDED') return 'locked';
  return 'active';
}

export type TeamAction = 'edit' | 'brands' | 'disable' | 'enable' | 'mfa-reset' | 'lock' | 'unlock' | 'delete';

/**
 * The actions one row offers, in the order they are drawn. The owner is
 * never offered disable or a brand limit (the server refuses both), and a
 * disabled account offers only enable. Delete comes last, for every member
 * including owners and disabled rows, to a viewer allowed to purge.
 */
export function rowActions(
  m: TeamMember,
  caps: TeamCapabilities,
  items: readonly TeamMember[] = [],
): TeamAction[] {
  const state = signInState(m);
  const out: TeamAction[] = [];
  // The last owner with nobody active to take over cannot be deleted at all:
  // the operator would be left with no owner.
  const deletable = caps.delete && !(needsSuccessor(m, items) && successorCandidates(m, items).length === 0);
  if (caps.manage) {
    if (state === 'disabled') return deletable ? ['enable', 'delete'] : ['enable'];
    out.push('edit');
    if (!m.is_owner) out.push('brands');
  }
  if (caps.credentials && state !== 'disabled') {
    if (m.mfa_enrolled) out.push('mfa-reset');
    out.push(state === 'locked' ? 'unlock' : 'lock');
  }
  if (caps.manage && !m.is_owner) out.push('disable');
  if (deletable) out.push('delete');
  return out;
}

/** The words for each action: its button, the dialog's title and body, and whether it is the risky kind. */
export const ACTION_WORDS: Readonly<Record<TeamAction, {
  readonly label: AmsMessageKey;
  readonly title: AmsMessageKey;
  readonly body: AmsMessageKey;
  readonly danger: boolean;
}>> = {
  edit: { label: 'ops.team.action.edit', title: 'ops.team.edit.title', body: 'ops.team.edit.body', danger: false },
  brands: { label: 'ops.team.action.brands', title: 'ops.team.brands.title', body: 'ops.team.brands.body', danger: false },
  disable: { label: 'ops.team.action.disable', title: 'ops.team.disable.title', body: 'ops.team.disable.body', danger: true },
  enable: { label: 'ops.team.action.enable', title: 'ops.team.enable.title', body: 'ops.team.enable.body', danger: false },
  'mfa-reset': { label: 'ops.team.action.mfaReset', title: 'ops.team.mfaReset.title', body: 'ops.team.mfaReset.body', danger: true },
  lock: { label: 'ops.team.action.lock', title: 'ops.team.lock.title', body: 'ops.team.lock.body', danger: true },
  unlock: { label: 'ops.team.action.unlock', title: 'ops.team.unlock.title', body: 'ops.team.unlock.body', danger: false },
  delete: { label: 'ops.team.action.delete', title: 'ops.team.delete.title', body: 'ops.team.delete.body', danger: true },
};

/** The member as one line: their name where known, otherwise their address. */
export function displayName(m: Pick<TeamMember, 'name' | 'email'>): string {
  return m.name?.trim() || m.email;
}

/** "All brands", or the brands they are limited to. */
export function brandSummary(m: Pick<TeamMember, 'brands'>, everyBrand: string): string {
  return m.brands.length === 0 ? everyBrand : m.brands.map((b) => b.name).join(', ');
}

export interface AddForm {
  readonly email: string;
  readonly name: string;
  readonly roleId: string;
  readonly brandIds: readonly string[];
  readonly reason: string;
}

export type AddProblem = 'email' | 'role' | 'reason';

/** The same shape the server accepts, without the quadratic email regex. */
function emailLooksOk(email: string): boolean {
  const text = email.trim();
  if (text.length === 0 || text.length > 254 || /\s/.test(text)) return false;
  const at = text.indexOf('@');
  if (at < 1 || at !== text.lastIndexOf('@')) return false;
  const dot = text.indexOf('.', at + 2);
  return dot !== -1 && text.length - dot - 1 >= 2;
}

/** What stops the add form being sent, so each field can say so. */
export function addProblems(f: AddForm): AddProblem[] {
  const out: AddProblem[] = [];
  if (!emailLooksOk(f.email)) out.push('email');
  if (!f.roleId) out.push('role');
  if (!f.reason.trim()) out.push('reason');
  return out;
}

/** The body an add sends. No password: the server mints one and mails it to the person alone. */
export function addBody(f: AddForm): {
  email: string; role_id: string; brand_ids: string[]; reason: string; name?: string;
} {
  return {
    email: f.email.trim(),
    role_id: f.roleId,
    brand_ids: [...f.brandIds],
    reason: f.reason.trim(),
    ...(f.name.trim() ? { name: f.name.trim() } : {}),
  };
}

/** What an edit sends: only what changed, so an unchanged role is not re-granted. */
export function editBody(
  m: TeamMember,
  roles: TeamData['roles'],
  draft: { readonly name: string; readonly roleId: string; readonly reason: string },
): { name?: string; role_id?: string; reason: string } | null {
  const current = roles.find((r) => m.roles.includes(r.code))?.id ?? '';
  const nameChanged = draft.name.trim() !== (m.name ?? '');
  const roleChanged = !m.is_owner && draft.roleId !== '' && draft.roleId !== current;
  if (!nameChanged && !roleChanged) return null;
  return {
    ...(nameChanged ? { name: draft.name.trim() } : {}),
    ...(roleChanged ? { role_id: draft.roleId } : {}),
    reason: draft.reason.trim(),
  };
}

/** True when removing this member would leave the operator with no live owner. */
export function needsSuccessor(m: TeamMember, items: readonly TeamMember[]): boolean {
  if (!m.is_owner) return false;
  return !items.some((o) => o.id !== m.id && o.is_owner && o.status !== 'CLOSED');
}

/** Who may take over: the other members who are ACTIVE (not locked, not disabled). */
export function successorCandidates(m: TeamMember, items: readonly TeamMember[]): TeamMember[] {
  return items.filter((o) => o.id !== m.id && o.status === 'ACTIVE');
}

export interface DeleteForm {
  readonly reason: string;
  readonly confirmEmail: string;
  readonly successorId: string;
}

export type DeleteProblem = 'reason' | 'confirm' | 'successor';

/**
 * What stops a delete being sent. The typed address must match the member's,
 * ignoring case and surrounding space; a successor is needed only for a sole owner.
 */
export function deleteProblems(f: DeleteForm, m: Pick<TeamMember, 'email'> & { readonly needsSuccessor: boolean }): DeleteProblem[] {
  const out: DeleteProblem[] = [];
  if (!f.reason.trim()) out.push('reason');
  if (f.confirmEmail.trim().toLowerCase() !== m.email.trim().toLowerCase()) out.push('confirm');
  if (m.needsSuccessor && !f.successorId) out.push('successor');
  return out;
}

/** What a delete sends. The successor is sent only when one was needed and chosen. */
export function deleteBody(f: DeleteForm): { reason: string; confirm_email: string; successor_id?: string } {
  return {
    reason: f.reason.trim(),
    confirm_email: f.confirmEmail.trim(),
    ...(f.successorId ? { successor_id: f.successorId } : {}),
  };
}

/** A friendlier line for the refusals a delete can meet; null leaves the server's own words. */
export function deleteFailureKey(code: string): AmsMessageKey | null {
  switch (code) {
    case 'REASON_REQUIRED': return 'ops.team.field.reasonMissing';
    case 'CONFIRMATION_MISMATCH': return 'ops.team.delete.mismatch';
    case 'CANNOT_DELETE_SELF': return 'ops.team.delete.err.self';
    case 'SUCCESSOR_REQUIRED': return 'ops.team.delete.err.successorRequired';
    case 'NO_SUCCESSOR_AVAILABLE': return 'ops.team.delete.none';
    case 'INVALID_SUCCESSOR': return 'ops.team.delete.err.successorInvalid';
    default: return null;
  }
}
